import { identity, keys, map, pickBy } from 'lodash';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Observable, Subject, catchError, of, take, takeUntil } from 'rxjs';
import { Component, OnInit, HostListener, ChangeDetectorRef, Renderer2, PLATFORM_ID, Inject } from '@angular/core';

import { InternalProductWithRatingsWithFavoriteStatusType } from '@sifca-monorepo/ecommerce-generator';
import { AppService, SeoService, SharedService, UtilsService } from '../../../services';


@Component({
  template: '',
})
export class BaseProductListTopFilterComponent implements OnInit {
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  seo: any;
  limit = 5;
  params: any;
  lastPage = 1;
  type = 'list';
  totalCount = 0;
  toggle = false;
  loaded = false;
  perPage: number;
  searchTerm = '';
  firstLoad = false;
  pageTitle = 'List';
  orderBy = 'default';
  filtredProducts: InternalProductWithRatingsWithFavoriteStatusType[];
  initialProducts: InternalProductWithRatingsWithFavoriteStatusType[];
  loadingProducts$: Observable<boolean>;

  constructor(
    protected router: Router,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected titleTagService: Title,
    protected seoService: SeoService,
    protected utilsService: UtilsService,
    protected activeRoute: ActivatedRoute,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platformId: Object,
    @Inject(DOCUMENT) protected document: Document,
  ) {
      this.loadingProducts$ = this.appService.loadingProducts$;
    this.appService.fromPrice = null;
    this.appService.toPrice = null;
    this.activeRoute.params.subscribe((params) => {
      this.type = params['type'];
      if (!this.type) {
        this.type = '4cols'
      }
      if (this.type === '1col') {
        this.pageTitle = 'List';
      } else if (this.type === '2cols') {
        this.pageTitle = 'Grid 2 Columns';
      } else if (this.type === '3cols') {
        this.pageTitle = 'Grid 3 Columns';
      } else if (this.type === '4cols') {
        this.pageTitle = 'Grid 4 Columns';
      }
    });
    this.activeRoute.queryParams.subscribe((params) => {
      if (params) {
        this.params = params;
        this.loaded = false;
        if (params['minPrice'] && params['maxPrice']) {
          this.appService.fromPrice = '' + params['minPrice'];
          this.appService.toPrice = '' + params['maxPrice'];
        }
        if (params['brand']) {
          this.appService.selectedBrands = params['brand'].split(',');
        }
        if (params['page']) {
          this.appService.productsPageIndex$ = params['page'];
        }
        if (params['category']) {
          this.appService.selectedCategories = params['category'].split(',');
        }
        if (params['categoryUrl']) {
          this.appService
            .getCategoryByUrl(params['categoryUrl'])
            .pipe(
              catchError(() => {
                return of(null);
              }),
            )
            .subscribe((res) => {
              this.appService.selectedCategories.push(res.id);
            });
        }
        if (params['searchTerm']) {
          this.searchTerm = params['searchTerm'];
          this.appService.searchString = params['searchTerm'];
        } else {
          this.searchTerm = '';
          this.appService.searchString = '';
        }
        if (params['orderBy']) {
          this.orderBy = params['orderBy'];
        } else {
          this.orderBy = 'default';
        }
      }
      if (!keys(params).length) {
        this.appService.selectedBrands = [];
        this.appService.selectedCategories = [];
        this.appService.productAttributes = [];
        this.appService.fromPrice = null;
        this.appService.toPrice = null;
      }
      this.refresh();
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (window.innerWidth > 991) {
        this.toggle = false;
      } else {
        this.toggle = true;
      }
    }
    this.appService.initialProducts$.pipe(takeUntil(this.unsubscribeAll)).subscribe((productsPaginated) => {
      if (productsPaginated) {
        this.initialProducts = productsPaginated.objects;
        this.changeDetectorRef.markForCheck();
      }
    });
    this.appService.filtredProducts$.pipe(takeUntil(this.unsubscribeAll)).subscribe((paginateResponse) => {
      this.loaded = false;
      if (paginateResponse) {
        this.filtredProducts = paginateResponse.objects;
        if (!this.firstLoad) {
          this.firstLoad = true;
        }
        this.loaded = true;
        this.totalCount = paginateResponse.count;
        this.perPage = this.appService?.productsLimit;
        if (paginateResponse.count >= this.limit) {
          this.lastPage = Math.ceil(paginateResponse?.count / (this.appService.productsLimit || this.perPage));
        } else {
          this.lastPage = 1;
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }
  getGeneralSEOJSON() {
    this.seoService
      .getGeneralSEOJSON()
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.sharedService.tags$ = [...this.metaTagService.addTags(map(this.seo?.metas, (meta) => pickBy(meta, identity))), { charset: 'UTF-8' }];
          this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.renderer.appendChild(this.document.head, link);
            return link;
          });
      });
  }

  refresh() {
    this.appService.productsPageIndex$ = this.params?.page - 1 || 0;
    this.getGeneralSEOJSON();
    this.appService
      .getSimpleProductsWithRatingsWithFavoriteStatusWithFilter()
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe();
  }

  @HostListener('window: resize', ['$event'])
  onResize(event: Event) {
    if (isPlatformBrowser(this.platformId)) {
      if (window.innerWidth > 991) {
        this.toggle = false;
      } else {
        this.toggle = true;
      }
    }
  }

  changeOrderBy(event: any) {
    this.router.navigate([], { queryParams: { orderBy: event.currentTarget.value, page: 1 }, queryParamsHandling: 'merge' });
  }

  sortBy(event: any) {
    this.filtredProducts = null;
    this.loaded = false;
    this.appService.createdAtSort = null;
    this.appService.nameSort = null;
    this.appService.priceSort = null;
    if (event) {
      event = JSON.parse(event.target.value);
      this.appService[event.field] = event.value;
    }
    this.appService
      .getSimpleProductsWithRatingsWithFavoriteStatusWithFilter()
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.loaded = true;
          this.utilsService.scrollToPageContent();
          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
